/**
 * Counselling Overview — the admin (`ActsCareAdmin`) reporting page at `/Counselling/Overview`.
 *
 * Org-wide care reporting: how much case work exists, how it is distributed, who is carrying it,
 * and what is stuck. Deliberately *not* personal; the per-user view lives on `/Dashboard`.
 * The course-side equivalent is `/Grow/Overview`.
 *
 * Data rules:
 *  - Every read goes through an SDK service hook. No `useHttpClient()` / `getApiUrl()`.
 *  - The shared hooks in `src/pages/Overview/Hooks/` take a domain, so this page fetches care data
 *    only. `dashboardStats()` polls every 60s; charts, workload and lists refresh on demand.
 *  - Server-side aggregates that do not exist in the SDK yet are specced in `_deploy/_newEndPoints/`.
 *    The hooks detect them by capability and fall back to grouping the streamed lists where that is
 *    possible; where it is not, the section says so rather than showing a misleading empty chart.
 */

import { useMemo } from 'react';
import {
  ActionIcon, Group, SimpleGrid, Stack, Text, Title, Tooltip, UnstyledButton,
} from '@mantine/core';
import {
  IconCalendarEvent, IconCalendarWeek, IconCircleCheck, IconClockExclamation, IconFileText,
  IconFolders, IconInbox, IconListDetails, IconRefresh, IconSparkles, IconUserOff, IconUsersGroup,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useCareCaseService, useCareCaseStatusService } from '@actschurch/ccms-sdk';
import { Stat, StatusKPICard, StatusKPISkeleton, getStatusIcon } from '@/components';
import { CASE_STATUS } from '@/pages/Counselling/caseStatusIds';
import ActivityMixBar from '@/pages/Overview/Components/ActivityMixBar';
import BreakdownBars from '@/pages/Overview/Components/BreakdownBars';
import CriticalListCard from '@/pages/Overview/Components/CriticalListCard';
import OverviewSection from '@/pages/Overview/Components/OverviewSection';
import WorkloadTable from '@/pages/Overview/Components/WorkloadTable';
import useLookupStatuses from '@/pages/Overview/Hooks/useLookupStatuses';
import useOverviewCharts from '@/pages/Overview/Hooks/useOverviewCharts';
import useOverviewLists from '@/pages/Overview/Hooks/useOverviewLists';
import useOverviewStats from '@/pages/Overview/Hooks/useOverviewStats';
import { useStatusCounts } from '@/pages/Overview/Hooks/useStatusCounts';

/** A KPI tile that navigates when it has somewhere to go. */
function LinkedStat({
  label, value, icon, to, description,
}: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  to?: string;
  description?: string;
}) {
  const navigate = useNavigate();
  const tile = <Stat label={label} value={value} icon={icon} description={description} />;

  if (!to) return tile;
  return (
    <UnstyledButton onClick={() => navigate(to)} style={{ textAlign: 'left' }}>
      {tile}
    </UnstyledButton>
  );
}

export default function CounsellingOverviewPage() {
  const navigate = useNavigate();

  const careCaseService = useCareCaseService();
  const caseStatusService = useCareCaseStatusService();

  const { careStats, loading: statsLoading, refresh: refreshStats } = useOverviewStats('care');
  const charts = useOverviewCharts('care');
  const lists = useOverviewLists('care');

  // Unlike `CasesListView`, which shows an include-list of four statuses, the Overview shows the
  // whole distribution — it is the one page whose job is the complete picture.
  const caseStatuses = useLookupStatuses(caseStatusService);

  const caseCounts = useStatusCounts(
    caseStatuses,
    (statusID) =>
      careCaseService.listView({ take: 1, skip: 0, statusID: String(statusID) })
        .then((r) => r.totalCount ?? 0),
    () => careCaseService.listView({ take: 1, skip: 0 }).then((r) => r.totalCount ?? 0),
  );

  const handleRefresh = () => {
    refreshStats();
    charts.refresh();
    lists.refresh();
    caseCounts.refresh();
  };

  const refreshing = statsLoading || charts.loading || lists.loading;

  const statusKpiSkeletons = useMemo(
    () => Array.from({ length: 5 }).map((_, i) => <StatusKPISkeleton key={i} />),
    [],
  );

  return (
    <Stack gap="xl">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Group justify="space-between" align="flex-end" wrap="nowrap">
        <div>
          <Title order={2}>Overview</Title>
          <Text c="dimmed" size="sm">
            Cases, sessions and the leaders carrying them, across the whole organisation.
          </Text>
        </div>
        <Tooltip label="Refresh">
          <ActionIcon variant="light" size="lg" onClick={handleRefresh} loading={refreshing}>
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <LinkedStat
          label="Pending Requests"
          value={careStats?.pendingRequestsCount ?? 0}
          icon={<IconInbox size={20} color="var(--mantine-color-orange-6)" />}
        />
        <LinkedStat
          label="Open Cases"
          value={careStats?.openCasesCount ?? 0}
          icon={<IconFolders size={20} color="var(--mantine-color-indigo-6)" />}
          to="/Counselling/Cases"
        />
        <LinkedStat
          label="Unassigned Cases"
          value={careStats?.unassignedCasesCount ?? 0}
          icon={<IconUserOff size={20} color="var(--mantine-color-red-6)" />}
          to="/Counselling/NewCases"
        />
        <LinkedStat
          label="Stale Cases"
          value={careStats?.staleCasesCount ?? 0}
          icon={<IconClockExclamation size={20} color="var(--mantine-color-orange-6)" />}
          to="/Counselling/Cases"
        />
        <LinkedStat
          label="Sessions Today"
          value={careStats?.sessionsTodayCount ?? 0}
          icon={<IconCalendarEvent size={20} color="var(--mantine-color-green-6)" />}
        />
        <LinkedStat
          label="Sessions This Week"
          value={careStats?.sessionsThisWeekCount ?? 0}
          icon={<IconCalendarWeek size={20} color="var(--mantine-color-teal-6)" />}
        />
        <LinkedStat
          label="Active Leaders"
          value={careStats?.activeLeadersCount ?? 0}
          icon={<IconUsersGroup size={20} color="var(--mantine-color-blue-6)" />}
          to="/Information/CareSupportLeaders"
        />
        <LinkedStat
          label="Closed This Month"
          value={careStats?.closedThisMonthCount ?? 0}
          icon={<IconCircleCheck size={20} color="var(--mantine-color-green-6)" />}
        />
      </SimpleGrid>

      <OverviewSection
        title="Cases by status"
        subtitle="Every status in the lookup, counted across all cases"
        viewAllTo="/Counselling/Cases"
      >
        <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="md">
          {caseCounts.loading && caseCounts.rows.length === 0 ? statusKpiSkeletons : (
            <>
              <StatusKPICard
                icon={IconListDetails}
                label="All"
                count={caseCounts.allCount}
                color="var(--mantine-primary-color-filled)"
                active={false}
                onClick={() => navigate('/Counselling/Cases')}
              />
              {caseCounts.rows.map((status) => (
                <StatusKPICard
                  key={status.statusID}
                  icon={getStatusIcon(status.icon ?? undefined)}
                  label={status.name ?? '—'}
                  count={status.count}
                  color={status.color || 'var(--mantine-primary-color-filled)'}
                  active={false}
                  onClick={() => navigate(
                    status.statusID === CASE_STATUS.New ? '/Counselling/NewCases'
                      : status.statusID === CASE_STATUS.Assigned ? '/Counselling/AssignedCases'
                      : status.statusID === CASE_STATUS.InProgress ? '/Counselling/InProgressCases'
                      : '/Counselling/Cases',
                  )}
                />
              ))}
            </>
          )}
        </SimpleGrid>
      </OverviewSection>

      <OverviewSection title="Where the need is" subtitle="Cases grouped by need, zone and location">
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <BreakdownBars
            title="Cases by need"
            subtitle="What people are actually asking for help with"
            data={charts.casesByNeed.data}
            unavailableMessage={charts.casesByNeed.unavailable}
            loading={charts.loading}
            entityLabel="needs"
            valueLabel="Cases"
            totalLabel="All time"
          />
          <BreakdownBars
            title="Cases by zone"
            subtitle="Geographic spread of the caseload"
            data={charts.casesByZone.data}
            unavailableMessage={charts.casesByZone.unavailable}
            loading={charts.loading}
            entityLabel="zones"
            valueLabel="Cases"
            totalLabel="All time"
          />
          <BreakdownBars
            title="Cases by location"
            subtitle="Which campuses are carrying the load"
            data={charts.casesByLocation.data}
            unavailableMessage={charts.casesByLocation.unavailable}
            loading={charts.loading}
            entityLabel="locations"
            valueLabel="Cases"
            totalLabel="All time"
          />
          <ActivityMixBar segments={charts.activityMix} loading={charts.loading} />
        </SimpleGrid>
      </OverviewSection>

      <OverviewSection
        title="Leader caseload"
        subtitle="Who is carrying how many active cases"
        viewAllTo="/Information/CareSupportLeaders"
      >
        <WorkloadTable
          title="Care Support Leaders"
          subtitle="Sorted by active cases"
          rows={lists.leaderWorkload}
          loading={lists.loading}
          primaryLabel="Active cases"
          secondaryLabel="Needs covered"
          rowRoutePrefix="/Information/CareSupportLeaders/Leaders/"
          viewAllTo="/Information/CareSupportLeaders"
          emptyMessage="No leaders have been set up yet."
        />
      </OverviewSection>

      <OverviewSection title="Needs attention" subtitle="Care work that is waiting on someone">
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <CriticalListCard
            title="New cases"
            description="Opened but not yet assigned to a leader"
            icon={IconSparkles}
            color="var(--mantine-color-blue-6)"
            rows={lists.newCases}
            totalCount={lists.newCasesTotal}
            loading={lists.loading}
            viewAllTo="/Counselling/NewCases"
            emptyMessage="No new cases waiting."
          />
          <CriticalListCard
            title="Unassigned cases"
            description="Open with no owning leader"
            icon={IconUserOff}
            color="var(--mantine-color-red-6)"
            rows={lists.unassignedCases}
            loading={lists.loading}
            viewAllTo={lists.unassignedAvailable ? '/Counselling/NewCases' : undefined}
            emptyMessage={
              lists.unassignedAvailable
                ? 'Every open case has a leader.'
                : 'Needs the unassigned-cases endpoint, see _deploy/_newEndPoints/.'
            }
          />
          <CriticalListCard
            title="Stale cases"
            description="No activity recorded recently"
            icon={IconClockExclamation}
            color="var(--mantine-color-orange-6)"
            rows={lists.staleCases}
            totalCount={lists.staleCasesTotal}
            loading={lists.loading}
            viewAllTo="/Counselling/Cases"
            emptyMessage="Nothing has gone quiet."
          />
          <CriticalListCard
            title="Pending care requests"
            description="Public requests awaiting triage"
            icon={IconInbox}
            color="var(--mantine-color-grape-6)"
            rows={lists.pendingRequests}
            totalCount={lists.pendingRequestsTotal}
            loading={lists.loading}
            viewAllTo="/Counselling/Requests"
            emptyMessage="No requests in the queue."
          />
        </SimpleGrid>
      </OverviewSection>

      {/* The opened-vs-closed trend needs a server-side monthly aggregate; it renders its own
          explanation until that ships. Kept last so the page never leads with a gap. */}
      {charts.caseTrend.unavailable && (
        <Text size="xs" c="dimmed">
          <IconFileText size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Monthly trend charts are pending the aggregates specced in <code>_deploy/_newEndPoints/</code>.
        </Text>
      )}
    </Stack>
  );
}
